import React, { useState, useEffect, useCallback, useRef } from "react";
import API_BASE_URL from "../../../components/apiconfig/api-config";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaFilter } from "react-icons/fa";

const WarehouseReportSummary = () => {
  const [reportData, setReportData] = useState(null);
  const [filteredData, setFilteredData] = useState([]);

  // ✅ Get current date in YYYY-MM-DD format for default To date
  const getCurrentDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // ✅ Updated filter state with date filters
  const [filters, setFilters] = useState({
    warehouseName: "",
    warehouseLocation: "",
    warehouseType: "",
    commodity: "",
    origin: "",
    liftingDateFrom: "", // ✅ Added
    liftingDateTo: getCurrentDate(), // ✅ Default to current date
  });
  const [showFilters, setShowFilters] = useState(false);
  const hasMounted = useRef(false);
  const prevDateFrom = useRef("");
  const prevDateTo = useRef("");

  const [filterOptions, setFilterOptions] = useState({
    warehouseNames: [],
    warehouseLocations: [],
    warehouseTypes: [],
    commodities: [],
    origins: [],
  });

  // ✅ Updated to include date filters in API call
  const handleGenerateReport = useCallback(async () => {
    try {
      // Build query string with date filters
      const queryParams = new URLSearchParams();
      if (filters.liftingDateFrom) {
        queryParams.append("liftingDateFrom", filters.liftingDateFrom);
      }
      if (filters.liftingDateTo) {
        queryParams.append("liftingDateTo", filters.liftingDateTo);
      }

      const url = `${API_BASE_URL}/api/report/warehouse-report-summary${
        queryParams.toString() ? "?" + queryParams.toString() : ""
      }`;

      const response = await fetch(url, {
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        setReportData(data.data);
        setFilteredData(data.data.summary_data);

        // Extract unique filter options
        const warehouseNames = [
          ...new Set(data.data.summary_data.map((item) => item.warehouse_name)),
        ];
        const locations = [
          ...new Set(
            data.data.summary_data.map((item) => item.warehouse_location)
          ),
        ];
        const types = [
          ...new Set(data.data.summary_data.map((item) => item.warehouse_type)),
        ];
        const commodities = [
          ...new Set(data.data.summary_data.map((item) => item.commodity)),
        ];
        const origins = [
          ...new Set(data.data.summary_data.map((item) => item.origin)),
        ];

        setFilterOptions({
          warehouseNames: warehouseNames,
          warehouseLocations: locations,
          warehouseTypes: types,
          commodities: commodities,
          origins: origins,
        });
      } else {
        toast.error(data.error || "Failed to generate report");
      }
    } catch (error) {
      console.error("Error generating report:", error);
      toast.error("Server Error");
    }
  }, [filters.liftingDateFrom, filters.liftingDateTo]);

  // Filter data based on selected filters (excluding date - handled by backend)
  useEffect(() => {
    if (!reportData) return;

    const filtered = reportData.summary_data.filter((item) => {
      const matchesWarehouseName =
        filters.warehouseName === "" ||
        item.warehouse_name === filters.warehouseName;
      const matchesWarehouseLocation =
        filters.warehouseLocation === "" ||
        item.warehouse_location === filters.warehouseLocation;
      const matchesWarehouseType =
        filters.warehouseType === "" ||
        item.warehouse_type === filters.warehouseType;
      const matchesCommodity =
        filters.commodity === "" || item.commodity === filters.commodity;
      const matchesOrigin =
        filters.origin === "" || item.origin === filters.origin;

      return (
        matchesWarehouseName &&
        matchesWarehouseLocation &&
        matchesWarehouseType &&
        matchesCommodity &&
        matchesOrigin
      );
    });

    setFilteredData(filtered);
  }, [
    filters.warehouseName,
    filters.warehouseLocation,
    filters.warehouseType,
    filters.commodity,
    filters.origin,
    reportData,
  ]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => {
      const updated = {
        ...prev,
        [field]: value,
      };
      
      // If "From" date is selected and it's greater than "To" date, update "To" date
      if (field === 'liftingDateFrom' && value && prev.liftingDateTo && value > prev.liftingDateTo) {
        updated.liftingDateTo = value;
      }
      
      return updated;
    });
  };

  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  const resetFilters = () => {
    setFilters({
      warehouseName: "",
      warehouseLocation: "",
      warehouseType: "",
      commodity: "",
      origin: "",
      liftingDateFrom: "",
      liftingDateTo: getCurrentDate(), // ✅ Reset to current date
    });
  };

  const handlePrint = () => {
    window.print();
  };

  // Auto-load report on component mount
  useEffect(() => {
    handleGenerateReport();
    hasMounted.current = true;
    prevDateFrom.current = filters.liftingDateFrom;
    prevDateTo.current = filters.liftingDateTo;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-refresh report when date filters change (after initial mount)
  useEffect(() => {
    // Skip on initial mount - handled by mount effect above
    if (!hasMounted.current) {
      return;
    }

    // Check if date filters actually changed
    const dateFromChanged = prevDateFrom.current !== filters.liftingDateFrom;
    const dateToChanged = prevDateTo.current !== filters.liftingDateTo;

    // Only trigger API call if dates actually changed
    if (dateFromChanged || dateToChanged) {
      handleGenerateReport();
      // Update previous values
      prevDateFrom.current = filters.liftingDateFrom;
      prevDateTo.current = filters.liftingDateTo;
    }
  }, [filters.liftingDateFrom, filters.liftingDateTo, handleGenerateReport]);

  return (
    <>
      {/* Print Styles */}
      <style>{`
        @media print {
          @page {
            size: A4 landscape;
            margin: 15mm 10mm;
          }
          
          body {
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }
          
          .no-print {
            display: none !important;
          }
          
          .shadow-md {
            box-shadow: none !important;
          }
          
          table {
            width: 100%;
            border-collapse: collapse !important;
            page-break-inside: auto;
          }
          
          tr {
            page-break-inside: avoid !important;
            page-break-after: auto;
            break-inside: avoid !important;
          }
          
          thead {
            display: table-header-group !important;
          }
          
          tfoot {
            display: table-footer-group !important;
          }
          
          tbody tr {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
          }
          
          .bg-blue-100 {
            background-color: #dbeafe !important;
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }
          
          .bg-blue-50 {
            background-color: #eff6ff !important;
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }
          
          .bg-gray-50 {
            background-color: #f9fafb !important;
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }
          
          .text-gray-800,
          .text-gray-700,
          .text-gray-600 {
            color: #000 !important;
          }
          
          td, th {
            padding: 6px 8px !important;
            word-wrap: break-word;
            overflow: visible;
          }
        }
      `}</style>

      <div>
        <div className="bg-white shadow-md p-6">
          {/* Header */}
          <div className="flex justify-between items-center mb-6 no-print">
            <h2 className="text-2xl font-semibold text-gray-800">
              Warehouse Report Summary
            </h2>
            <div className="flex gap-2">
              <button
                onClick={toggleFilters}
                className="flex items-center gap-2 px-4 py-2 bg-blue-100 text-[#3899cf] rounded-md hover:bg-blue-200 transition"
              >
                <FaFilter />
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
              <button
                onClick={handlePrint}
                disabled={!reportData || filteredData.length === 0}
                className="px-4 py-2 bg-[#3899cf] text-white rounded hover:bg-[#0e8dc6] font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Print Report
              </button>
            </div>
          </div>

          {/* ✅ Filter Section with Date Filters */}
          {showFilters && (
            <div className="mb-6 p-4 bg-gray-50 rounded-lg no-print">
              <div className="flex flex-wrap gap-4 mb-4"></div>

              <div className="flex flex-wrap gap-4">
                {/* Warehouse Name */}
                <div className="flex flex-col w-48">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Warehouse Name
                  </label>
                  <select
                    name="warehouseName"
                    value={filters.warehouseName}
                    onChange={(e) =>
                      handleFilterChange("warehouseName", e.target.value)
                    }
                    className="p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">All Warehouses</option>
                    {filterOptions.warehouseNames.map((name, index) => (
                      <option key={index} value={name}>
                        {name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Warehouse Location */}
                <div className="flex flex-col w-48">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Warehouse Location
                  </label>
                  <select
                    name="warehouseLocation"
                    value={filters.warehouseLocation}
                    onChange={(e) =>
                      handleFilterChange("warehouseLocation", e.target.value)
                    }
                    className="p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">All Locations</option>
                    {filterOptions.warehouseLocations.map((location, index) => (
                      <option key={index} value={location}>
                        {location}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Warehouse Type */}
                <div className="flex flex-col w-48">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Warehouse Type
                  </label>
                  <select
                    name="warehouseType"
                    value={filters.warehouseType}
                    onChange={(e) =>
                      handleFilterChange("warehouseType", e.target.value)
                    }
                    className="p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">All Types</option>
                    {filterOptions.warehouseTypes.map((type, index) => (
                      <option key={index} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Commodity */}
                <div className="flex flex-col w-48">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Commodity
                  </label>
                  <select
                    name="commodity"
                    value={filters.commodity}
                    onChange={(e) =>
                      handleFilterChange("commodity", e.target.value)
                    }
                    className="p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">All Commodities</option>
                    {filterOptions.commodities.map((commodity, index) => (
                      <option key={index} value={commodity}>
                        {commodity}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Origin */}
                <div className="flex flex-col w-48">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Origin
                  </label>
                  <select
                    name="origin"
                    value={filters.origin}
                    onChange={(e) =>
                      handleFilterChange("origin", e.target.value)
                    }
                    className="p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">All Origins</option>
                    {filterOptions.origins.map((origin, index) => (
                      <option key={index} value={origin}>
                        {origin}
                      </option>
                    ))}
                  </select>
                </div>
               {/* ✅ Lifting Date From */}
<div className="flex flex-col w-48">
  <label className="block text-sm font-medium text-gray-700 mb-1">From</label>
  <input
    type="date"
    name="liftingDateFrom"
    value={filters.liftingDateFrom}
    onChange={(e) => handleFilterChange('liftingDateFrom', e.target.value)}
    max={filters.liftingDateTo || getCurrentDate()} // Prevent selecting date after "To" date
    onKeyDown={(e) => e.preventDefault()} // Disable manual typing
    className="p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
  />
</div>

                {/* To */}
                <div className="flex flex-col w-48">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    To
                  </label>
                  <input
                    type="date"
                    name="liftingDateTo"
                    value={filters.liftingDateTo}
                    onChange={(e) =>
                      handleFilterChange("liftingDateTo", e.target.value)
                    }
                    min={filters.liftingDateFrom || undefined} // Prevent selecting date before "From" date
                    max={getCurrentDate()} // Block future dates
                    onKeyDown={(e) => e.preventDefault()} // Disable manual typing
                    className="p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* Reset Button */}
                <div className="flex items-end">
                  <button
                    onClick={resetFilters}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition"
                  >
                    Reset Filters
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Report Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm border border-gray-200 rounded">
              <thead className="bg-blue-100 sticky top-0 z-10">
                <tr>
                  <th className="px-4 py-3 text-left text-gray-700 font-medium border">
                    Warehouse Name
                  </th>
                  <th className="px-4 py-3 text-left text-gray-700 font-medium border">
                    Warehouse Location
                  </th>
                  <th className="px-4 py-3 text-left text-gray-700 font-medium border">
                    Warehouse Type
                  </th>
                  <th className="px-4 py-3 text-left text-gray-700 font-medium border">
                    Vessel Name
                  </th>
                  <th className="px-4 py-3 text-left text-gray-700 font-medium border">
                    Commodity
                  </th>
                  <th className="px-4 py-3 text-left text-gray-700 font-medium border">
                    Origin
                  </th>
                  <th className="px-4 py-3 text-center text-gray-700 font-medium border">
                    From
                  </th>
                  <th className="px-4 py-3 text-center text-gray-700 font-medium border">
                    To
                  </th>
                  <th className="px-4 py-3 text-center text-gray-700 font-medium border">
                    Opening Stock (MT)
                  </th>
                  <th className="px-4 py-3 text-center text-gray-700 font-medium border">
                    Inward Qty (MT)
                  </th>
                  <th className="px-4 py-3 text-center text-gray-700 font-medium border">
                    Outward Qty (MT)
                  </th>
                  <th className="px-4 py-3 text-center text-gray-700 font-medium border">
                    Adjusted Qty (MT)
                  </th>
                  <th className="px-4 py-3 text-center text-gray-700 font-medium border">
                    Closing Stock (MT)
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredData.length > 0 ? (
                  <>
                    {filteredData.map((item, index) => (
                      <tr
                        key={index}
                        className="hover:bg-blue-50 transition duration-200 ease-in-out"
                      >
                        <td className="px-4 py-2 border font-medium text-gray-800">
                          {item.warehouse_name}
                        </td>
                        <td className="px-4 py-2 border text-gray-600">
                          {item.warehouse_location}
                        </td>
                        <td className="px-4 py-2 border text-gray-700">
                          {item.warehouse_type}
                        </td>
                        <td className="px-4 py-2 border text-gray-700">
                          {item.vessel_name}
                        </td>
                        <td className="px-4 py-2 border text-gray-700">
                          {item.commodity}
                        </td>
                        <td className="px-4 py-2 border text-gray-600">
                          {item.origin}
                        </td>
                        <td className="px-4 py-2 text-center border text-gray-700">
                          {item.from_date || "01/01/2025"}
                        </td>
                        <td className="px-4 py-2 text-center border text-gray-700">
                          {item.to_date ||
                            new Date().toLocaleDateString("en-IN", {
                              day: "2-digit",
                              month: "2-digit",
                              year: "numeric",
                            })}
                        </td>
                        <td className="px-4 py-2 text-right border font-semibold text-gray-800">
                          {item.opening_stock.toFixed(3)}
                        </td>
                        <td className="px-4 py-2 text-right border font-semibold text-gray-800">
                          {item.inward_qty.toFixed(3)}
                        </td>
                        <td className="px-4 py-2 text-right border font-semibold text-gray-800">
                          {item.outward_qty.toFixed(3)}
                        </td>
                        <td className={`px-4 py-2 text-right border font-semibold ${item.adjusted_qty > 0 ? 'text-green-600' : item.adjusted_qty < 0 ? 'text-red-600' : 'text-gray-800'}`}>
                          {item.adjusted_qty > 0 ? `+${item.adjusted_qty.toFixed(3)}` : item.adjusted_qty ? item.adjusted_qty.toFixed(3) : '0.000'}
                        </td>
                        <td className="px-4 py-2 text-right border font-semibold text-gray-800">
                          {item.closing_stock.toFixed(3)}
                        </td>
                      </tr>
                    ))}
                    {/* Totals row */}
                    {reportData && (
                      <tr className="bg-gray-50 font-bold">
                        <td
                          colSpan="8"
                          className="px-4 py-3 border text-right text-gray-700"
                        >
                          Subtotal:
                        </td>
                        <td className="px-4 py-3 text-right border text-gray-800">
                          {filteredData
                            .reduce((sum, item) => sum + item.opening_stock, 0)
                            .toFixed(3)}
                        </td>
                        <td className="px-4 py-3 text-right border text-gray-800">
                          {filteredData
                            .reduce((sum, item) => sum + item.inward_qty, 0)
                            .toFixed(3)}
                        </td>
                        <td className="px-4 py-3 text-right border text-gray-800">
                          {filteredData
                            .reduce((sum, item) => sum + item.outward_qty, 0)
                            .toFixed(3)}
                        </td>
                        <td className="px-4 py-3 text-right border font-bold">
                          {(() => {
                            const totalAdj = filteredData.reduce((sum, item) => sum + (item.adjusted_qty || 0), 0);
                            return (
                              <span className={totalAdj > 0 ? 'text-green-600' : totalAdj < 0 ? 'text-red-600' : 'text-gray-800'}>
                                {totalAdj > 0 ? `+${totalAdj.toFixed(3)}` : totalAdj.toFixed(3)}
                              </span>
                            );
                          })()}
                        </td>
                        <td className="px-4 py-3 text-right border text-gray-800">
                          {filteredData
                            .reduce((sum, item) => sum + item.closing_stock, 0)
                            .toFixed(3)}
                        </td>
                      </tr>
                    )}
                  </>
                ) : (
                  <tr>
                    <td
                      colSpan="13"
                      className="text-center p-8 text-gray-500 italic"
                    >
                      {!reportData
                        ? "Loading warehouse data..."
                        : "No warehouse data found for the selected filters."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          pauseOnFocusLoss
          draggable
          pauseOnHover
          className="no-print"
        />
      </div>
    </>
  );
};

export default WarehouseReportSummary;
